import useGetConnectionApplication from '@/api/queries/connectionApplication/useGetConnectionApplication';
import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import {
  FormAndJSONSchema,
  FormSection,
} from '@/api/rest/connectionApplication/useGetConnectionApplicationSchema';
import { generateFormSectionsForScrollSpy } from '@/ui/components/enaForms/utils/display';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { FC, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router';
import {
  prepareFormSchema,
  prepareJSONSchema,
  RemovedFields,
} from '../../components/enaForms/utils/reactHookForm';
import ConnectionApplicationSkeleton from '../connectionApplicationPage/ConnectionApplicationSkeleton';
import CommissioningForm from './CommissioningForm';
import CommissioningLayout from './CommissioningLayout';
import { ConnectionApplicationDataSchemaForCommissioning } from './connectionApplicationDataSchema';
import DnoResponseCard from './DnoResponseCard';

const CommissioningPage: FC = () => {
  const { projectId, connectionApplicationId } = useParams<{
    projectId: string;
    connectionApplicationId: string;
  }>();

  const { project } = useGetProjectByIdOverview(projectId);

  const connectionApplication = useGetConnectionApplication(
    connectionApplicationId,
    false
  ).data?.connectionApplication;

  const unenhancedFormSchema = connectionApplication?.commissioning?.form
    .schema as FormAndJSONSchema | undefined;

  const [jsonSchema, formSchema] = useMemo(() => {
    if (unenhancedFormSchema) {
      const removedFields: RemovedFields = { files: true };
      const jsonSchema = prepareJSONSchema(unenhancedFormSchema, removedFields);
      const formSchema = prepareFormSchema(unenhancedFormSchema);

      // In the devices section, each device's system reference is in a
      // fixed input, which would normally be hidden. We override it to
      // display it as the title of the device card, so you can see which
      // device the section refers to.
      const deviceSysRefSchema =
        formSchema.properties?.devices?.items?.properties?.deviceSysRef;
      if (deviceSysRefSchema) {
        deviceSysRefSchema.json_schema_extra.type = 'FIXED INPUT AS TITLE';
      }

      // The devices section needs to be completed for each device in
      // the original application. We set the min and max items to both
      // match the number of devices, so it renders as a fixed number of
      // subforms, without "add" or "remove" buttons.
      if (formSchema.properties?.devices?.type === 'array') {
        const connectionApplicationData =
          ConnectionApplicationDataSchemaForCommissioning.safeParse(
            connectionApplication?.data
          ).data;

        if (connectionApplicationData) {
          // Filter to only generation devices (those with generatorType)
          const generationDevices =
            connectionApplicationData.devicesToInstall.filter(
              (device) => device.generatorType != null
            );
          const numberOfDevices = generationDevices.length;
          if (numberOfDevices > 0) {
            formSchema.properties.devices.minItems = numberOfDevices;
            formSchema.properties.devices.maxItems = numberOfDevices;
          }
        }
      }

      return [jsonSchema, formSchema];
    } else {
      return [undefined, undefined];
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'connectionApplication.data'
  }, [unenhancedFormSchema]);

  const subSections = Object.entries<FormSection>(formSchema?.properties ?? {});

  const dnoResponseScrollSection = {
    label: 'DNO Response',
    slug: 'dno-response',
    render: () => (
      <DnoResponseCard
        commissioningStatus={
          connectionApplication?.commissioning?.commissioningStatus
        }
        dnoComments={connectionApplication?.commissioning?.dnoComments}
        exportMpan={connectionApplication?.commissioning?.exportMpan}
      />
    ),
  };

  let commissioningContent = <ConnectionApplicationSkeleton />;
  if (
    formSchema &&
    connectionApplication &&
    connectionApplication.commissioning
  ) {
    commissioningContent = connectionApplication.commissioning?.submittedAt ? (
      <CommissioningLayout
        sections={[
          dnoResponseScrollSection,
          ...generateFormSectionsForScrollSpy({
            formSections: subSections,
            formSchema,
            readonly: true,
            formData: connectionApplication.commissioning?.data,
          }),
        ]}
      />
    ) : (
      <CommissioningForm
        projectId={projectId}
        connectionApplication={connectionApplication}
        jsonSchema={jsonSchema}
        formSchema={formSchema}
        subSections={subSections}
      />
    );
  }

  return (
    <>
      <Helmet title={'Commissioning'} />
      <AuthenticatedPageWrapper
        breadcrumbItems={[
          {
            label: `Projects`,
            to: `/projects`,
          },
          {
            label: `Project: #${project?.reference || ''}`,
            to: `/projects/${projectId}/property`,
          },
          {
            label: 'DNO application',
            to: `/projects/${projectId}/compliance/connection-application/${connectionApplicationId}`,
          },
          {
            label: 'Commissioning',
          },
        ]}
      >
        {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values */}
        <div className="mx-auto max-w-[96rem] px-4">{commissioningContent}</div>
      </AuthenticatedPageWrapper>
    </>
  );
};
export default CommissioningPage;
